import type { JSX } from 'react';
import type {
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  LexicalEditor,
  LexicalNode,
  NodeKey,
  SerializedLexicalNode,
  Spread,
} from 'lexical';
import { $applyNodeReplacement, DecoratorNode } from 'lexical';
import { ImageComponent } from './ImageComponent';

export interface ImagePayload {
  altText?: string;
  height?: 'auto' | number;
  src: string;
  width?: 'inherit' | number;
}

export type SerializedImageNode = Spread<
  {
    altText: string;
    height: 'auto' | number;
    src: string;
    type: 'image';
    version: 1;
    width: 'inherit' | number;
  },
  SerializedLexicalNode
>;

function convertImageElement(
  domNode: globalThis.HTMLElement
): DOMConversionOutput | null {
  if (!(domNode instanceof globalThis.HTMLImageElement)) {
    return null;
  }

  return {
    node: $createImageNode({
      altText: domNode.alt,
      height: parseHeightAttribute(domNode.getAttribute('height')),
      src: domNode.getAttribute('src') ?? '',
      width: parseWidthAttribute(domNode.getAttribute('width')),
    }),
  };
}

function parseHeightAttribute(value: string | null) {
  if (!value) {
    return 'auto' as const;
  }

  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : ('auto' as const);
}

function parseWidthAttribute(value: string | null) {
  if (!value) {
    return 'inherit' as const;
  }

  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : ('inherit' as const);
}

export class ImageNode extends DecoratorNode<JSX.Element> {
  __altText: string;
  __height: 'auto' | number;
  __src: string;
  __width: 'inherit' | number;

  static getType(): string {
    return 'image';
  }

  static clone(node: ImageNode): ImageNode {
    return new ImageNode(
      node.__src,
      node.__altText,
      node.__width,
      node.__height,
      node.__key
    );
  }

  static importDOM(): DOMConversionMap | null {
    return {
      img: () => ({
        conversion: convertImageElement,
        priority: 0,
      }),
    };
  }

  static importJSON(serializedNode: SerializedImageNode): ImageNode {
    return $createImageNode(serializedNode).updateFromJSON(serializedNode);
  }

  constructor(
    src: string,
    altText = '',
    width: 'inherit' | number = 'inherit',
    height: 'auto' | number = 'auto',
    key?: NodeKey
  ) {
    super(key);
    this.__src = src;
    this.__altText = altText;
    this.__width = width;
    this.__height = height;
  }

  exportDOM(): DOMExportOutput {
    const imageElement = globalThis.document.createElement('img');
    imageElement.setAttribute('src', this.__src);
    imageElement.setAttribute('alt', this.__altText);

    if (typeof this.__width === 'number') {
      imageElement.setAttribute('width', String(this.__width));
    }

    if (typeof this.__height === 'number') {
      imageElement.setAttribute('height', String(this.__height));
    }

    return { element: imageElement };
  }

  exportJSON(): SerializedImageNode {
    return {
      altText: this.getAltText(),
      height: this.__height,
      src: this.getSrc(),
      ...super.exportJSON(),
      type: 'image',
      version: 1,
      width: this.__width,
    };
  }

  updateFromJSON(serializedNode: SerializedImageNode): this {
    const writable = this.getWritable();
    writable.__altText = serializedNode.altText;
    writable.__height = serializedNode.height;
    writable.__src = serializedNode.src;
    writable.__width = serializedNode.width;
    return writable;
  }

  setAltText(altText: string): this {
    const writable = this.getWritable();
    writable.__altText = altText;
    return writable;
  }

  setHeight(height: 'auto' | number): this {
    const writable = this.getWritable();
    writable.__height = height;
    return writable;
  }

  setSrc(src: string): this {
    const writable = this.getWritable();
    writable.__src = src;
    return writable;
  }

  setWidth(width: 'inherit' | number): this {
    const writable = this.getWritable();
    writable.__width = width;
    return writable;
  }

  getAltText(): string {
    return this.getLatest().__altText;
  }

  getHeight(): 'auto' | number {
    return this.getLatest().__height;
  }

  getSrc(): string {
    return this.getLatest().__src;
  }

  getWidth(): 'inherit' | number {
    return this.getLatest().__width;
  }

  createDOM(): globalThis.HTMLElement {
    const element = globalThis.document.createElement('span');
    element.className = 'inline-editor-image';
    return element;
  }

  updateDOM(): false {
    return false;
  }

  isInline(): true {
    return true;
  }

  decorate(editor: LexicalEditor): JSX.Element {
    return (
      <ImageComponent
        altText={this.__altText}
        editor={editor}
        height={this.__height}
        nodeKey={this.getKey()}
        src={this.__src}
        width={this.__width}
      />
    );
  }
}

export function $createImageNode({
  altText = '',
  height = 'auto',
  src,
  width = 'inherit',
}: ImagePayload): ImageNode {
  return $applyNodeReplacement(
    new ImageNode(src, altText, width, height)
  );
}

export function $isImageNode(
  node: LexicalNode | null | undefined
): node is ImageNode {
  return node instanceof ImageNode;
}
